import React, { useState } from 'react';
import '../LeftColumn.css';
import { NavLink } from 'react-router-dom';
import axios from 'axios';
import Snackbar from '@mui/material/Snackbar';
import IconButton from '@mui/material/IconButton';
import CloseIcon from '@mui/icons-material/Close';
import ReCAPTCHA from "react-google-recaptcha";
import { useAuth } from '../contexts/AuthContext.jsx';
import Login from './Login.jsx';
import { Button } from '@mui/material';


function LeftColumn() {
    const [email, setEmail] = useState('');
    const [isInputActive, setIsInputActive] = useState(false);
    const [openSnackbar, setOpenSnackbar] = useState(false);
    const [snackbarMessage, setSnackbarMessage] = useState("");
    const [captchaValue, setCaptchaValue] = useState(null);
    const [loginOpen, setLoginOpen] = useState(false);
    const recaptchaRef = React.useRef(null);
    const siteKey = import.meta.env.VITE_RECAPTCHA_SITE_KEY;
    
    // Debug logging for environment variables
    React.useEffect(() => {
        console.log('Environment variables:', {
            siteKey: siteKey ? 'present' : 'missing',
            apiUrl: import.meta.env.VITE_API_URL
        });
    }, [siteKey]);
    
    const { isAuthenticated, logout } = useAuth();

    const handleCaptchaResponseChange = (value) => {
        setCaptchaValue(value);
    };

    const handleSubmit = async (event) => {
        event.preventDefault();

        if (siteKey && !captchaValue) {
            setSnackbarMessage("Please complete the CAPTCHA.");
            setOpenSnackbar(true);
            return;
        }        
        
        try {
            // Construct the full API URL from the environment variable
            const apiUrl = `${import.meta.env.VITE_API_URL}/submit-email`;
            const response = await axios.post(apiUrl, { 
                email: email, 
                captchaValue: captchaValue 
            });
            
            // Assuming your server responds with a message on a successful email submission
            setSnackbarMessage(response.data.message || "Email ontvangen!");
            setOpenSnackbar(true);
            recaptchaRef.current?.reset(); // <-- Reset CAPTCHA here
            
            console.log(response.data.message);
        } catch (error) {
            let errorMessage = "Er ging iets mis. Probeer het later nog eens.";

            // Check for a more specific error message from the server's response
            if (error.response && error.response.data) {
                // Handle custom error messages (e.g., "Email already subscribed")
                if (error.response.data.message) {
                    errorMessage = error.response.data.message;
                } 
                // Handle validation errors from express-validator
                else if (error.response.data.errors && error.response.data.errors.length > 0) {
                    const firstError = error.response.data.errors[0];
                    if (firstError.path === 'email') {
                        errorMessage = "Voer alstublieft een geldig emailadres in.";
                    } else {
                        errorMessage = firstError.msg;
                    }
                }
            }
            
            setSnackbarMessage(errorMessage);
            setOpenSnackbar(true);
            recaptchaRef.current?.reset(); // <-- Reset CAPTCHA here too
    
            // Log the detailed error from the server for better debugging
            console.error('Error submitting email:', error.response ? error.response.data : error.message);
        }
    };
    

    return (
        <div className="left-column">
            <img src="/MDA_Logo.svg" alt="MDA Logo" className="logo" />

            <p className="subheader">Vanuit Maarten Delen Advies help ik op interimbasis scholen als tijdelijke directeur, locatiedirecteur of teamleider. Ik voer adviesopdrachten uit op het terrein van taakbeleid, kwaliteitszorg en interne veiligheid. Via de website deel ik mijn Ultra Korte Verhalen en de presentaties van mijn boekbesprekingen.</p>

            <hr className="divider" />

            <nav className="navigation">
                <NavLink to="/" className='navigation'>Home</NavLink>
                <NavLink to="/CV" className='navigation'>CV</NavLink>
                <NavLink to="/about" className='navigation'>Over Maarten</NavLink>
                <NavLink to="/contact" className='navigation'>Contact</NavLink>
                <NavLink to="/stories" className='navigation'>Ultra Korte Verhalen</NavLink>
                {isAuthenticated && (
                    <NavLink to="/admin" className='navigation'>Admin</NavLink>
                )}
            </nav>

            <hr className="divider" />

            <div className="newsletter-box">
                <h3>UKV&apos;s direct in je mailbox ontvangen?</h3>
                <p>Vul hier je emailadres in.</p>
                <div className="newsletter-form-row">
                    <input
                        type="email"
                        placeholder="Email"
                        value={email}
                        onChange={e => setEmail(e.target.value)}
                        onFocus={() => setIsInputActive(true)}
                        required
                    />
                    <button className="btn" onClick={handleSubmit}>Verstuur</button>
                </div>

                {/* Conditionally render reCAPTCHA here */}
                {isInputActive && (
                    siteKey ? (
                        <ReCAPTCHA
                            ref={recaptchaRef}
                            sitekey={siteKey}
                            onChange={handleCaptchaResponseChange}
                            style={{ marginTop: '16px' }}
                        />
                    ) : (
                        <div style={{ marginTop: '16px', padding: '8px', backgroundColor: '#ffebee', color: '#c62828', borderRadius: '4px' }}>
                            reCAPTCHA configuration missing. Please check environment variables.
                        </div>
                    )
                )}
            </div>

            <hr className="divider" />

            <p className="footer-info">Maarten Delen Advies <br></br>KvK: 83819541 <br></br>BTW: NL003876750B29 <br></br>Bank: NL 49 TRIO 0320 3203 16</p>

            <hr className="divider" />

            <div className="auth-section">
                {isAuthenticated ? (
                    <Button 
                        variant="outlined" 
                        size="small" 
                        onClick={logout}
                        sx={{ width: '100%', mb: 1 }}
                    >
                        Logout
                    </Button>
                ) : (
                    <Button 
                        variant="outlined" 
                        size="small" 
                        onClick={() => setLoginOpen(true)}
                        sx={{ width: '100%', mb: 1 }}
                    >
                        Admin Login
                    </Button>
                )}
            </div>
        
            <Snackbar
                anchorOrigin={{
                    vertical: 'bottom',
                    horizontal: 'left',
                }}
                open={openSnackbar}
                autoHideDuration={6000}
                onClose={() => setOpenSnackbar(false)}
                message={snackbarMessage}
                action={
                    <>
                        <IconButton size="small" aria-label="close" color="inherit" onClick={() => setOpenSnackbar(false)}>
                            <CloseIcon fontSize="small" />
                        </IconButton>
                    </>
                }
            />
            
            <Login 
                open={loginOpen} 
                onClose={() => setLoginOpen(false)}
            />
        </div>
    );
}

export default LeftColumn;
